import moment from "moment";
import { useState } from "react";
import { api } from "../../../api/API";
import { TaskInput, TaskLink } from "../../../api/Tasks";
import { dateFormat } from "../../../misc/dates";
import { applyOrder } from "../../../misc/dragOrder";
import { useSnackAction } from "../../../misc/SnackAction";
import { useSyncedRef } from "../../../misc/SyncedRef";
import { ChecklistDraftRow } from "./ChecklistDraftRow";
import { PanelAddField } from "./PanelSection";
import { TaskLinkSection, toLink } from "./TaskLinkSection";

// TaskChecklist breaks a task into sub-tasks. An item is edited in the list itself
// rather than in a panel of its own, since there is little to it besides a title and
// a date.
export function TaskChecklist(props: {
    taskId: number;
    value: TaskLink[];
    onChange: (value: TaskLink[]) => void;

    // the task's own completion and due date, which set the day its items are
    // expected to be ticked off
    completedAt: string | null;
    dueDate: string | null;
}) {
    // an item is added without waiting for the last one, so the list it joins is read
    // as the answer lands rather than as the title was typed
    const valueRef = useSyncedRef(props.value);

    // the list holds a summary, so the whole item is loaded to save a change back to it
    const edit = async (id: number, changes: TaskInput) => {
        const item = await api.tasks.get({ id: id });

        return api.tasks.upsert({
            ...item,
            ...changes,
        });
    };

    const replace = (saved: TaskLink) =>
        props.onChange(valueRef.current.map(link => link.id === saved.id ? saved : link));

    const add = useSnackAction(async (input: { title: string }) => {
        const item = await api.tasks.upsert({
            type: "checklist",
            parent: props.taskId,
            title: input.title,
        });

        props.onChange(valueRef.current.concat(toLink(item)));
    }, { success: "Added" });

    // the draft being typed in the middle of the list, after the item with this id
    const [draftAfter, setDraftAfter] = useState<number | null>(null);

    const insert = useSnackAction(async (input: { title: string; after: number }) => {
        const item = toLink(await api.tasks.upsert({
            type: "checklist",
            parent: props.taskId,
            title: input.title,
        }));

        const next = valueRef.current.slice();
        next.splice(next.findIndex(link => link.id === input.after) + 1, 0, item);

        await api.tasks.reorderChecklist({ ids: next.map(link => link.id) });
        props.onChange(next);

        // the next draft follows the item just saved, unless the draft was dropped meanwhile
        setDraftAfter(after => after === null ? null : item.id);
    }, { success: "Added" });

    const reorder = useSnackAction(async (input: { ids: number[] }) => {
        await api.tasks.reorderChecklist(input);
        props.onChange(applyOrder(valueRef.current, input.ids));
    }, { success: "Saved" });

    const rename = useSnackAction(async (input: { id: number; title: string }) => {
        replace(toLink(await edit(input.id, { title: input.title })));
    }, { success: "Saved" });

    const schedule = useSnackAction(async (input: { id: number; dueDate: string; dueTime: string }) => {
        replace(toLink(await edit(input.id, {
            dueDate: input.dueDate || null,
            dueTime: input.dueTime || null,
        })));
    }, { success: "Saved" });

    const toggle = useSnackAction(async (input: { id: number; completed: boolean }) => {
        replace(toLink(await api.tasks.complete(input)));
    }, { success: "Saved" });

    // an item has no life outside the task it belongs to, so removing it archives it
    const archive = useSnackAction(async (input: { id: number }) => {
        await edit(input.id, { archived: true });

        props.onChange(valueRef.current.filter(link => link.id !== input.id));
    }, { success: "Removed" });

    const promote = useSnackAction(async (input: { id: number }) => {
        await api.tasks.promote(input);

        props.onChange(valueRef.current.filter(link => link.id !== input.id));
    }, { success: "Moved to a standalone task" });

    const expectedDate = props.completedAt ? moment(props.completedAt).format(dateFormat) : props.dueDate;

    const busy = add.loading || insert.loading || reorder.loading || rename.loading || schedule.loading || toggle.loading || archive.loading || promote.loading;

    return (
        <TaskLinkSection
            value={props.value}
            busy={busy}
            expectedDate={expectedDate}
            onRename={(link, title) => rename.callback({ id: link.id, title: title })}
            onEnter={link => setDraftAfter(link.id)}
            onReorder={ids => reorder.callback({ ids: ids })}
            insert={draftAfter === null ? undefined : {
                after: draftAfter,
                element: (
                    <ChecklistDraftRow
                        saving={insert.loading}
                        onSubmit={title => insert.callback({ title: title, after: draftAfter })}
                        onCancel={() => setDraftAfter(null)}
                    />
                ),
            }}
            onSchedule={(link, date) => schedule.callback({ id: link.id, ...date })}
            onToggle={(link, completed) => toggle.callback({ id: link.id, completed: completed })}
            onRemove={link => archive.callback({ id: link.id })}
            onArchive={link => archive.callback({ id: link.id })}
            removeArchives
            onPromote={link => promote.callback({ id: link.id })}
        >
            <PanelAddField
                placeholder="Add an item"
                onAdd={title => add.callback({ title: title })}
            />
        </TaskLinkSection>
    );
}
